import React from 'react';
import styled from 'styled-components';
import { Lock } from 'lucide-react';
import useEventAccess from 'hooks/useEventAccess';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

/**
 * No lugar de uma página do evento a que o convite não dá acesso: diz que é
 * restrito e a quem pedir, e mostra riscos no lugar do conteúdo.
 *
 * @param {{ title?: string, needs?: string }} props `needs` é o que precisa ser
 *   liberado, em texto ("ver vendas, pedidos e valores").
 */
export default function RestrictedPage({ title = 'Você não tem acesso a esta página', needs }) {
    const { owner } = useEventAccess();
    const ownerName = owner?.name || 'o organizador';

    return (
        <Wrap role='status'>
            <IconWrap>
                <Lock size={20} color={panelAccent(colors.secondaryPurple)} aria-hidden='true' />
            </IconWrap>

            <Title>{title}</Title>

            <Text>
                {needs
                    ? <>{ownerName} não liberou <strong>{needs}</strong> para você neste evento. Peça ao organizador para incluir essa permissão.</>
                    : <>Esta página é só de {ownerName}, o organizador do evento.</>}
            </Text>

            <Bars aria-hidden='true'>
                <Bar width='62%' />
                <Bar width='84%' />
                <Bar width='48%' />
            </Bars>
        </Wrap>
    );
}

const Wrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 40px 20px;
    border-radius: 14px;
    background: ${colors.neutralLight};
    border: 1px dashed ${colors.nightLight};
    text-align: center;
`;

const IconWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: ${panelAccentAlpha(colors.secondaryPurple, '14')};
`;

const Title = styled.p`
    margin: 0;
    font-size: ${fonts.sizes.highlight};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const Text = styled.p`
    margin: 0;
    max-width: 460px;
    font-size: ${fonts.sizes.default};
    line-height: 1.5;
    color: ${colors.night};
`;

const Bars = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 100%;
    max-width: 360px;
    margin-top: 12px;
`;

const Bar = styled.span`
    width: ${({ width }) => width};
    height: 10px;
    border-radius: 6px;
    background: ${colors.neutralDark};
`;
